// Components
import { Button, Divider, Stack, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import type { UseFormReturnType } from "@mantine/form";
import { FormSection } from "@benchmark/ui/components/atomic";
import { ContactInput } from "./ContactInput";

// Constants
import {
  CODE_SYSTEM,
  getContactRelationshipOptions,
  getPatientFamilyContactRelationshipOptions,
} from "@benchmark/core/constants";
import { partitionPatientContacts } from "@benchmark/core/adapters";

// Types
import type { PatientFormData } from "@benchmark/core/types";
import type { PatientRelatedPersonContact } from "@benchmark/core/types";

/**
 * Props for ContactsSection component.
 * @agentic @benchmark/core
 */
type ContactsSectionProps = {
  form: UseFormReturnType<PatientFormData>;
  title?: string;
  subtitle?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
};

const createEmptyContact = (
  relationshipSystem: string,
): PatientRelatedPersonContact => ({
  "first-name": "",
  "last-name": "",
  phone: "",
  relationship: "",
  "relationship-system": relationshipSystem,
});

/**
 * Form section for managing additional patient contacts (family, friends).
 * @agentic @benchmark/core
 */
export const ContactsSection = ({
  form,
  title = "Additional Contacts",
  subtitle = "Add family members, caregivers, or other contacts for this member.",
  collapsible = false,
  defaultCollapsed = false,
}: ContactsSectionProps) => {
  const contacts: PatientRelatedPersonContact[] =
    form.values["patient-contacts"] || [];
  const generalRelationshipOptions = getContactRelationshipOptions();
  const familyRelationshipOptions =
    getPatientFamilyContactRelationshipOptions();
  const { general, family } = partitionPatientContacts(contacts);
  const contactsError = form.errors["patient-contacts"] as string | undefined;

  // Parse error message to extract contact index and field
  // Error format: "Contact {index} requires a {field}" or "Contact {index}: {error}"
  const parseError = (
    error: string | undefined,
  ): {
    contactIndex: number | null;
    field: keyof PatientRelatedPersonContact | null;
  } => {
    if (!error) return { contactIndex: null, field: null };

    const match = error.match(/Contact (\d+)/);
    if (!match) return { contactIndex: null, field: null };

    const contactIndex = parseInt(match[1], 10) - 1; // Convert to 0-based

    // Determine field from error message
    let field: keyof PatientRelatedPersonContact | null = null;
    if (error.includes("first name")) field = "first-name";
    else if (error.includes("last name")) field = "last-name";
    else if (error.includes("phone")) field = "phone";
    else if (error.includes("relationship")) field = "relationship";

    return { contactIndex, field };
  };

  const errorInfo = parseError(contactsError);

  const handleAddContact = (relationshipSystem: string) => {
    const updatedContacts = [
      ...contacts,
      createEmptyContact(relationshipSystem),
    ];
    form.setFieldValue("patient-contacts", updatedContacts);
  };

  const handleRemoveContact = (index: number) => {
    const updatedContacts = contacts.filter((_, i) => i !== index);
    form.setFieldValue("patient-contacts", updatedContacts);
  };

  const handleUpdateContact = (
    index: number,
    field: keyof PatientRelatedPersonContact,
    value: string,
  ) => {
    const updatedContacts = [...contacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value,
    };
    form.setFieldValue("patient-contacts", updatedContacts);
  };

  const renderContactRows = (
    entries: Array<{ contact: PatientRelatedPersonContact; index: number }>,
    relationshipOptions: Array<{ value: string; label: string }>,
  ) =>
    entries.map(({ contact, index }) => (
      <ContactInput
        key={index}
        index={index}
        contact={contact}
        onUpdate={handleUpdateContact}
        onRemove={handleRemoveContact}
        relationshipOptions={relationshipOptions}
        getFieldError={(field) =>
          errorInfo.contactIndex === index && errorInfo.field === field
            ? contactsError
            : undefined
        }
      />
    ));

  return (
    <FormSection
      title={title}
      subtitle={subtitle}
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
    >
      <Stack gap="md">
        {contacts.length === 0 ? (
          <Text c="dimmed" size="sm">
            No contacts added yet. Add a general contact or a family and
            caregiver contact below.
          </Text>
        ) : (
          <>
            {general.length > 0 && (
              <Stack gap="md">
                <Text size="sm" fw={500}>
                  General contacts
                </Text>
                {renderContactRows(general, generalRelationshipOptions)}
              </Stack>
            )}

            {general.length > 0 && family.length > 0 && <Divider />}

            {family.length > 0 && (
              <Stack gap="md">
                <Text size="sm" fw={500}>
                  Family and caregivers
                </Text>
                {renderContactRows(family, familyRelationshipOptions)}
              </Stack>
            )}
          </>
        )}

        <Button
          leftSection={<IconPlus size={16} />}
          variant="light"
          onClick={() => handleAddContact(CODE_SYSTEM.V2_0131)}
          aria-label="Add contact"
        >
          Add Contact
        </Button>

        <Button
          leftSection={<IconPlus size={16} />}
          variant="light"
          onClick={() => handleAddContact(CODE_SYSTEM.V3_ROLE_CODE)}
          aria-label="Add family or caregiver contact"
        >
          Add Family / Caregiver Contact
        </Button>
      </Stack>
    </FormSection>
  );
};
